import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { Link } from 'react-router-dom';

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #316C6E;
  }
`;

export const Button = styled.button`
  padding: 12px;
  background: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;

  &:hover {
    background: #254F51;
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 40px; // Make room for the eye icon
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #316C6E;
  }

  &:focus {
    outline: none;
  }
`;

export const PasswordField = ({ value, onChange, placeholder }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].auth;

  return (
    <PasswordContainer>
      <PasswordInput
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder || t.password}
        required
      />
      <PasswordToggle
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        title={showPassword ? 'Hide password' : 'Show password'}
      >
        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
      </PasswordToggle>
    </PasswordContainer>
  );
};

export const LoginForm = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { language } = useLanguage();
  const t = translations[language].auth;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ email, password });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        type="email"
        placeholder={t.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <PasswordField
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Button type="submit">{t.signIn}</Button>
    </Form>
  );
};

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
`;

const CheckboxLabel = styled.label`
  color: #666;
  font-size: 14px;
  line-height: 1.4;
`;

const TermsLink = styled(Link)`
  color: #316C6E;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const SignupForm = ({ onSubmit }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].auth;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      // You might want to show an error message here
      return;
    }
    onSubmit({ firstName, lastName, email, password });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        type="text"
        placeholder={t.firstName}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
      />
      <Input
        type="text"
        placeholder={t.lastName}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
      />
      <Input
        type="email"
        placeholder={t.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <PasswordField
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id="terms"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          required
        />
        <CheckboxLabel htmlFor="terms">
          {language === 'en' ? 'I accept the ' : 'Acepto los '}
          <TermsLink to="/legal/terms" target="_blank">
            {language === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
          </TermsLink>
        </CheckboxLabel>
      </CheckboxContainer>
      <Button type="submit">{t.signUp}</Button>
    </Form>
  );
}; 